// Lib Imports
import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import { Box, Button, Text, Card, CardBody, Heading, Grid, Image } from 'grommet';
import { FormDown, Add } from 'grommet-icons';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';

// Core Imports
import FormField from 'granite-admin/core/components/FormField';
import Loader from 'granite-admin/core/components/Loader';
import Split from 'granite-admin/core/components/Split';
import { useToast } from 'granite-admin/core/components/Toast';
import Select from 'granite-admin/core/components/Select';
import NoRecordsPlaceholder from 'granite-admin/core/components/NoRecordsPlaceholder';
import { ConfigContext } from '../ConfigProvider';

// Application Imports
import * as OrganisationDucks from 'granite-admin/organisations/ducks/organisations';
import { fetchNotes, createNoteClicked } from 'granite-admin/common/controllers/notes';
import EventEmitter from 'granite-admin/utils/event-emitter';
import { getOptionsConfig } from 'granite-admin/common/controllers/common';
import { getRolesByOrganisation } from 'granite-admin/organisations/controllers/organisation';
import { fetchBAdminRoles } from 'granite-admin/common/controllers/businessAdmin';
import employeeAPI from 'granite-admin/organisations/gateways/employees-api';
import { getBAdmins } from 'granite-admin/common/gateways/business-admin-api';
import defaultIcon from 'granite-admin/common/assets/default_profile.png';
import MentionBox from 'granite-admin/core/components/MentionBox';
import Tooltip from 'granite-admin/core/components/Tooltip';
import SaveIcon from 'granite-admin/common/assets/SaveIcon';
import { NotesSkeletonLoader, NotesFormSkeletonLoader } from './components/NotesSkeletonLoader';
import cancel from 'granite-admin/common/assets/cancel.svg';
const StyledNoteCardBox = styled(Card)`
  background: ${({
  type
}) => type.toLowerCase() === 'info' ? '#D1ECF1' : type.toLowerCase() === 'alert' ? '#f8D7DA' : '#FFF3CD'};
`;
const NoteCard = ({
  note
}) => {
  return /*#__PURE__*/React.createElement(Box, {
    margin: "xsmall",
    flex: {
      shrink: 0
    },
    pad: "xsmall",
    gap: "small"
  }, /*#__PURE__*/React.createElement(StyledNoteCardBox, {
    type: note.type
  }, /*#__PURE__*/React.createElement(CardBody, {
    pad: "small",
    gap: "small"
  }, /*#__PURE__*/React.createElement(Grid, {
    columns: ['85%', 'auto']
  }, /*#__PURE__*/React.createElement(Text, {
    dangerouslySetInnerHTML: {
      __html: note.note.replaceAll('\n', '</br>')
    }
  }), /*#__PURE__*/React.createElement(Box, {
    gap: "small",
    direction: "row",
    justify: "end",
    align: "center",
    pad: {
      bottom: 'small'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    background: note.type === 'Info' ? 'lightgreen' : note.type === 'Warning' ? 'red' : 'yellow',
    width: "10px",
    height: "10px",
    round: "50%"
  }), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "bold"
  }, note.type))), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "#707070",
    alignSelf: "end"
  }, note.date, " (", note.name, ")"))));
};
NoteCard.propTypes = {
  note: PropTypes.object
};
const LoadingUI = () => /*#__PURE__*/React.createElement(Box, {
  pad: "medium",
  align: "center"
}, /*#__PURE__*/React.createElement(Loader, null));
const customOptions = ({
  name,
  eventColor,
  eventId,
  ...rest
}, {
  context
}) => context !== 'value' ? /*#__PURE__*/React.createElement(Box, {
  direction: "row",
  fill: true,
  flex: true,
  justify: "between",
  pad: "0px 2px"
}, /*#__PURE__*/React.createElement(Box, {
  direction: "row",
  justify: "between",
  align: "center",
  fill: true
}, /*#__PURE__*/React.createElement(Text, null, name), /*#__PURE__*/React.createElement(Box, {
  background: name === 'Info' ? 'lightgreen' : name === 'Warning' ? 'red' : 'yellow',
  width: "10px",
  height: "10px",
  round: "50%"
}))) : /*#__PURE__*/React.createElement(Text, {
  style: {
    whiteSpace: 'nowrap'
  }
}, name);
const Notes = ({
  selectedOrganisation,
  setShowNotes,
  objectId,
  contentTypeName,
  notesName = 'Notes'
}) => {
  const [notesData, setNotesData] = useState({
    notes: [],
    nextPage: 1,
    count: 0
  });
  const {
    successToast,
    errorToast
  } = useToast();
  const eventEmitter = useMemo(() => new EventEmitter(), []);
  const [noteType, setNoteType] = useState('');
  const [loader, setLoader] = useState(false);
  const [noteTypeOptions, setNoteTypeOptions] = useState([]);
  const [userRoles, setUserRoles] = useState(null);
  const [userList, setUserList] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const config = useContext(ConfigContext);
  useEffect(() => {
    getOptionsConfig(eventEmitter, ['note_type']);
  }, [eventEmitter]);
  const handleGetNotes = useCallback((nextPage, payloadKey, note_type) => {
    let type = note_type || noteType;
    if (type === 'ALL') type = '';
    const obj = {
      page: nextPage,
      target_content_type_name: contentTypeName,
      target_object_id: objectId,
      note_type: type
    };
    setLoader(true);
    fetchNotes(eventEmitter, obj, payloadKey);
  }, [noteType, contentTypeName, objectId, eventEmitter]);
  useEffect(() => {
    if (notesData.notes.length || !notesData.nextPage) return;
    handleGetNotes(1);
  }, [notesData.notes, notesData.nextPage, handleGetNotes]);
  useEffect(() => {
    const subscription = eventEmitter.getObservable().subscribe(event => {
      switch (event.type) {
        case 'NOTES_FETCH_SUCCESS':
          if (event.data.page === 1) {
            setNotesData(notesData => ({
              notes: _.uniqBy([...event.data.notes], 'pk'),
              nextPage: event.data.nextPage,
              count: event.data.count
            }));
          } else {
            setNotesData(notesData => ({
              notes: _.uniqBy([...notesData.notes, ...event.data.notes], 'pk'),
              nextPage: event.data.nextPage,
              count: event.data.count
            }));
          }
          setLoader(false);
          break;
        case 'NOTES_FETCH_SUCCESS_ONSAVE':
          setNotesData(() => ({
            notes: [...event.data.notes],
            nextPage: event.data.nextPage,
            count: event.data.count
          }));
          setLoader(false);
          if (notesData.notes.length > 0) notesCardContainer.current.scroll({
            top: 0,
            behavior: 'smooth'
          });
          break;
        case 'NOTES_FETCH_FAILURE':
          errorToast('Failed to fetch notes!');
          setLoader(false);
          break;
        case 'FETCH_CONFIG_SUCCESS':
          // eslint-disable-next-line no-case-declarations
          const noteTypeArr = event.data.length && event.data[0].possible_values ? event.data[0].possible_values : [];
          setNoteTypeOptions(noteTypeArr);
          break;
        case 'NOTES_SAVE_SUCCESS':
          handleGetNotes(1);
          successToast('Note added successfully');
          setLoader(false);
          break;
        case 'NOTES_SAVE_FAILURE':
          errorToast('Failed to save note!');
          if (!notesData.nextPage) {
            const lastPage = Math.ceil(notesData.count / 10) || 1;
            handleGetNotes(lastPage);
          }
          break;
        default:
          console.log('Not handled', event);
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [eventEmitter, successToast, errorToast, handleGetNotes, notesData.count, notesData.nextPage, notesData.notes.length]);
  const fetchUsers = useCallback(async search => {
    const data = await employeeAPI.getEmployees({
      page: 1,
      page_size: 50,
      is_active: true,
      search: search || ''
    });
    return data.list.map(i => ({
      pk: i.user_pk,
      name: i.name,
      entity: 'user',
      title: `(${i.designation})`,
      avatar: i.profileImage || defaultIcon
    }));
  }, []);
  const fetchBusinessAdmins = useCallback(async search => {
    const data = await getBAdmins({
      page: 1,
      page_size: 50,
      is_active: true,
      search: search || ''
    });
    return data.list?.map(i => ({
      pk: i.userPk,
      name: i.name,
      entity: 'user',
      title: `(${i.designation || '-'})`,
      avatar: i.profileImage || defaultIcon
    }));
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = [],
          roles = [];
        if (!selectedOrganisation || !Object.keys(selectedOrganisation)?.length) {
          result = await Promise.all([fetchBAdminRoles({
            page: 1,
            page_size: 1000
          }), fetchBusinessAdmins('')]);
          roles = result[0].list.map(i => ({
            pk: i.pk,
            name: i.name,
            entity: 'role',
            title: '(User Role)',
            avatar: defaultIcon
          }));
        } else {
          result = await Promise.all([getRolesByOrganisation(selectedOrganisation), fetchUsers('')]);
          roles = result[0].roles.map(i => ({
            pk: i.pk,
            name: i.name,
            entity: 'role',
            title: '(User Role)',
            avatar: defaultIcon
          }));
        }
        setUserRoles(roles);
        setUserList(result[1]);
      } catch (e) {
        setUserRoles([]);
        setUserList([]);
        errorToast('Failed to get data!');
      }
    };
    fetchData();
  }, [selectedOrganisation, errorToast, fetchUsers, fetchBusinessAdmins]);
  const handleEdit = useCallback(() => {
    setIsEditable(prev => !prev);
  }, []);
  const notesCardContainer = useRef(null);
  return /*#__PURE__*/React.createElement(Box, {
    flex: "grow"
  }, /*#__PURE__*/React.createElement(Split, {
    setShow: setShowNotes,
    heading: notesName,
    FlexGrow: ['auto', 1],
    FlexBasis: ['auto', 'auto'],
    upperPane: /*#__PURE__*/React.createElement(React.Fragment, null, !isEditable ? /*#__PURE__*/React.createElement(Button, {
      label: 'Add ' + notesName,
      icon: /*#__PURE__*/React.createElement(Add, {
        size: "small",
        color: "white"
      }),
      primary: true,
      onClick: handleEdit,
      margin: {
        bottom: 'medium'
      }
    }) : /*#__PURE__*/React.createElement(CreateNoteForm, {
      handleEdit: handleEdit,
      eventEmitter: eventEmitter,
      createNoteClicked: createNoteClicked,
      objectId: objectId,
      contentTypeName: contentTypeName,
      noteTypeOptions: noteTypeOptions,
      userRoles: userRoles,
      userList: userList,
      fetchUsers: fetchUsers,
      notesName: notesName,
      config: config
    })),
    bottomPane: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      justify: "between",
      align: "center",
      border: {
        side: 'top',
        size: '1px',
        color: 'brand'
      }
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Heading, {
      level: 6,
      size: "xlarge",
      margin: "none"
    }, "Previous ", notesName), ' '), /*#__PURE__*/React.createElement(Box, {
      width: "40%"
    }, /*#__PURE__*/React.createElement(FormField, {
      label: `Filter ${notesName} by type`
    }, /*#__PURE__*/React.createElement(Select, {
      name: "filter",
      options: noteTypeOptions.length ? noteTypeOptions.map(i => {
        return {
          name: i,
          value: i
        };
      }) : [],
      labelKey: "name",
      valueKey: {
        key: 'value',
        reduce: true
      },
      value: noteType !== 'ALL' ? noteType : null
      // isClearable={false}
      ,
      onChange: ({
        value
      }) => {
        if (!value) value = 'ALL';
        setNoteType(value);
        if (value) handleGetNotes(1, 'nextPage', value);else handleGetNotes(1, 'nextPage', 'ALL');
      },
      formatOptionLabel: customOptions
    })))), /*#__PURE__*/React.createElement(Box, {
      ref: notesCardContainer,
      pad: {
        horizontal: 'xxsmall'
      },
      flex: "grow",
      overflow: {
        vertical: 'auto'
      },
      height: "0px"
    }, loader && config?.showSkeletonLoader ? /*#__PURE__*/React.createElement(NotesSkeletonLoader, null) : loader && /*#__PURE__*/React.createElement(LoadingUI, null), !loader && notesData.notes.length === 0 &&
    /*#__PURE__*/
    // <Box flex="grow" pad="xsmall" gap="medium" justify="center" align="center">
    //   <Text color="status-disabled" weight="600">
    //     No Records Found
    //   </Text>
    // </Box>
    React.createElement(NoRecordsPlaceholder, {
      msg: `No ${notesName} Found`,
      textProps: {
        size: 'large'
      }
    }), notesData.notes.map(note => /*#__PURE__*/React.createElement(NoteCard, {
      note: note,
      key: note.pk
    })), !loader && notesData.nextPage && /*#__PURE__*/React.createElement(Box, {
      flex: {
        shrink: 0
      },
      alignSelf: "center",
      align: "center",
      onClick: () => handleGetNotes(notesData.nextPage, 'nextPage'),
      margin: "xsmall"
    }, /*#__PURE__*/React.createElement(Text, {
      size: "small",
      color: "info"
    }, "Load more"), /*#__PURE__*/React.createElement(FormDown, {
      size: "small",
      color: "info"
    }))))
  }));
};
const CreateNoteForm = ({
  eventEmitter,
  createNoteClicked,
  objectId,
  contentTypeName,
  noteTypeOptions,
  userRoles,
  userList,
  fetchUsers,
  notesName,
  config,
  handleEdit
}) => {
  const [mentions, setMentions] = useState([]);
  const [resetEditor, setResetEditor] = useState(false);
  const validationSchema = Yup.object().shape({
    note: Yup.string().required('Note is required'),
    type: Yup.string().nullable().required('Note type is required')
  });
  const onSubmit = async (values, {
    setSubmitting,
    setErrors,
    resetForm
  }) => {
    setErrors({});
    setSubmitting(true);
    let visibility = {
      user_ids: [],
      role_id: []
    };
    mentions.forEach(obj => {
      if (obj.entity === 'user') visibility.user_ids.push(obj.pk);else if (obj.entity === 'role') visibility.role_id.push(obj.pk);
    });
    try {
      const obj = {
        target_content_type_name: contentTypeName,
        target_object_id: objectId,
        note: values.note,
        note_type: values.type,
        visibility: visibility
      };
      await createNoteClicked(eventEmitter, obj);
      resetForm();
      setResetEditor(!resetEditor);
    } catch (e) {
      if (e.errors) setErrors(e.errors);
    }
    setSubmitting(false);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, userRoles && userList ? /*#__PURE__*/React.createElement(Formik, {
    initialValues: {
      type: '',
      note: ''
    },
    onSubmit: onSubmit,
    enableReinitialize: true,
    validationSchema: validationSchema
  }, ({
    values,
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    isValid,
    dirty
  }) => {
    return /*#__PURE__*/React.createElement(Box, {
      pad: "small"
    }, /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(Text, {
      as: "div",
      color: "status-critical"
    }, errors.title), /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      justify: "between",
      align: "center",
      pad: {
        right: 'medium'
      }
    }, /*#__PURE__*/React.createElement(FormField, {
      name: "type",
      width: "medium",
      error: touched.type && errors.type
    }, /*#__PURE__*/React.createElement(Select, {
      placeholder: `Select ${notesName.slice(0, -1).toLowerCase()} type`,
      name: "type",
      options: noteTypeOptions.length ? noteTypeOptions.map(i => {
        return {
          name: i,
          value: i
        };
      }) : [],
      labelKey: "name",
      valueKey: {
        key: 'value',
        reduce: true
      },
      value: values.type,
      onChange: ({
        value
      }) => setFieldValue('type', value),
      onBlur: e => setFieldTouched('type', true),
      formatOptionLabel: customOptions
    })), /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      gap: "small",
      weight: "bold"
    }, /*#__PURE__*/React.createElement(Button, {
      label: /*#__PURE__*/React.createElement(Tooltip, {
        align: "bottom",
        content: "Close"
      }, /*#__PURE__*/React.createElement(Image, {
        margin: {
          right: 'small'
        },
        width: "20px",
        height: "20px",
        src: cancel
      })),
      onClick: handleEdit,
      disabled: isSubmitting,
      plain: true
    }), /*#__PURE__*/React.createElement(Button, {
      type: "submit",
      label: /*#__PURE__*/React.createElement(Tooltip, {
        align: "bottom",
        content: 'Save ' + notesName
      }, /*#__PURE__*/React.createElement(SaveIcon, null)),
      disabled: isSubmitting || !(isValid && dirty),
      plain: true
    }))), /*#__PURE__*/React.createElement(Box, {
      pad: {
        top: 'medium'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "bold",
      size: "medium"
    }, notesName.slice(0, -1)), /*#__PURE__*/React.createElement(FormField, {
      name: "note",
      error: touched.note && errors.note,
      noBorder: true
    }, /*#__PURE__*/React.createElement(MentionBox, {
      key: resetEditor,
      name: "note",
      setMentions: setMentions,
      setFieldValue: setFieldValue,
      userRoles: userRoles,
      userList: userList,
      fetchUsers: fetchUsers,
      editorProps: {
        placeholder: `Enter your ${notesName.slice(0, -1).toLowerCase()} here`
      }
    })))));
  }) : config?.showSkeletonLoader ? /*#__PURE__*/React.createElement(NotesFormSkeletonLoader, null) : /*#__PURE__*/React.createElement(LoadingUI, null));
};
CreateNoteForm.propTypes = {
  eventEmitter: PropTypes.object,
  createNoteClicked: PropTypes.func,
  selectedOrganisation: PropTypes.object,
  notesName: PropTypes.string,
  objectId: PropTypes.string,
  contentTypeName: PropTypes.string,
  noteTypeOptions: PropTypes.any,
  userRoles: PropTypes.any,
  userList: PropTypes.any,
  fetchUsers: PropTypes.any,
  config: PropTypes.object,
  handleEdit: PropTypes.func
};
Notes.propTypes = {
  eventEmitter: PropTypes.object,
  selectedOrganisation: PropTypes.object,
  messages: PropTypes.object,
  notesData: PropTypes.object,
  handleGetNotes: PropTypes.func,
  createNoteClicked: PropTypes.func,
  notesName: PropTypes.string,
  setShowNotes: PropTypes.func,
  objectId: PropTypes.string,
  contentTypeName: PropTypes.string
};
const mapStateToProps = state => ({
  selectedOrganisation: OrganisationDucks.selectedOrganisation(state)
});
export default connect(mapStateToProps)(Notes);